import { ArrowUpRight, Check } from 'lucide-react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import FaqAccordion, { type FaqItem } from '@/components/marketing/FaqAccordion'
import { EmailForm, FlashMessages, HelpLine, STILL_CARD, type SignInPageProps } from '@/components/marketing/signin/SignInParts'

/**
 * /log-in — an organic landing page for "music distribution dashboard"
 * queries, so the h1 carries that phrase whatever else changes.
 *
 * Two doors while `distribution_dashboard` is present: the external
 * Distribution Dashboard and the Account Hub. A super admin removes the first
 * (Settings → System → Log-in page) once the Account Hub holds everything; the
 * prop is then null and the page is the Account Hub sign-in alone — no card,
 * no link, no URL anywhere in the payload.
 */
interface LoginProps extends SignInPageProps {
  /** Account Hub sign-in is paused for a maintenance window. */
  maintenance: boolean
  distribution_dashboard: { url: string } | null
}

const DASHBOARD_FEATURES = [
  'Upload and schedule releases — 100+ stores in 1–4 days',
  'Daily stream analytics and royalty statements',
  'Revenue splits, Content ID and track metadata',
]

// The Account Hub once it is the only dashboard — it holds everything.
const ONE_DASHBOARD_FEATURES = [
  'Release music to 100+ stores and follow every release',
  'Royalty statements, analytics and payouts to your bank',
  'Playlist pitches, YouTube OAC and promo tools',
  'Support from real people, inside your dashboard',
]

const HUB_FEATURES = [
  'Withdraw royalties straight to your bank or wallet',
  'Playlist pitches, YouTube OAC, social linking',
  'Application status and support from real people',
]

function faqItems(twoDashboards: boolean): FaqItem[] {
  return [
    ...(twoDashboards
      ? [{
          question: 'Which dashboard do I need?',
          answer: 'The Distribution Dashboard is where you upload releases and read your streams. The Account Hub is ' +
                  'where you withdraw royalties, request playlist pitches and YouTube OAC, follow your application ' +
                  'and talk to support. Most artists use both.',
        }]
      : []),
    {
      question: 'How do I sign in to the Account Hub?',
      answer: 'Enter the email address you applied with. We send a 6-digit code to that inbox — there is no ' +
              'password to remember. The code works for 10 minutes; ask for a new one if it runs out.',
    },
    {
      question: 'The code has not arrived.',
      answer: 'Check your spam or promotions folder, then use “Resend code” on the next screen. If it still does ' +
              'not arrive, write to help@wavbee.com from the same address.',
    },
    {
      question: 'I do not have an account yet.',
      answer: 'wavbee is application-only. Apply as an artist or a label and we will review your application — ' +
              'once you are accepted, this page signs you in.',
    },
  ]
}

function FeatureList({ items, tone, large = false }: { items: string[]; tone: 'accent' | 'muted'; large?: boolean }) {
  return (
    <ul className={large ? 'space-y-4' : 'space-y-2.5'}>
      {items.map((item) => (
        <li key={item} className={`flex items-start gap-3 text-white/80 ${large ? 'text-base' : 'text-sm'}`}>
          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${tone === 'accent' ? 'text-[#ff8c4d]' : 'text-white/60'}`} aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  )
}

function AccountHubCard({ props }: { props: LoginProps }) {
  const twoDashboards = props.distribution_dashboard !== null
  return (
    <section aria-labelledby="account-hub-title" className="marketing-surface-card flex h-full flex-col" style={STILL_CARD}>
      <span className="marketing-eyebrow">Account Hub · one-time code</span>
      <h2 id="account-hub-title" className="mt-4 text-2xl font-bold tracking-tight text-white">Account Hub</h2>
      <p className="mt-2 text-sm text-white/70">Payouts, promo and support — handled.</p>
      <div className="mt-6"><FeatureList items={HUB_FEATURES} tone="muted" /></div>
      <div className="mt-8 border-t border-white/10 pt-6">
        {props.maintenance ? <Maintenance twoDashboards={twoDashboards} /> : <EmailForm props={props} />}
      </div>
    </section>
  )
}

function DistributionDashboardCard({ url }: { url: string }) {
  const host = url.replace(/^https?:\/\//, '').replace(/\/$/, '')
  return (
    <a
      href={url}
      aria-labelledby="distribution-dashboard-title"
      className="marketing-surface-card group flex h-full flex-col border-[#ff6a1a]/30 transition-colors hover:border-[#ff6a1a]/60"
    >
      <span className="flex items-center justify-between gap-3">
        <span className="marketing-eyebrow">Distribution</span>
        <span className="font-mono text-xs text-[#ff8c4d]/70">{host}</span>
      </span>
      <h2 id="distribution-dashboard-title" className="mt-4 text-2xl font-bold tracking-tight text-white">Distribution Dashboard</h2>
      <p className="mt-2 text-sm text-white/70">Where your music lives and earns.</p>
      <div className="mt-6"><FeatureList items={DASHBOARD_FEATURES} tone="accent" /></div>
      <span className="mt-auto flex items-center justify-between border-t border-white/10 pt-6 text-sm font-semibold text-white">
        Open dashboard
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#ff6a1a] text-[#1a0800] transition-transform group-hover:translate-x-1">
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </span>
    </a>
  )
}

function Maintenance({ twoDashboards }: { twoDashboards: boolean }) {
  return (
    <div role="status">
      <p className="text-sm font-semibold text-white">Account Hub sign-in is paused</p>
      <p className="mt-1 text-sm text-white/70">
        Brief scheduled maintenance is in progress, so Account Hub sign-in is temporarily unavailable and
        will be back shortly.{twoDashboards && ' The Distribution Dashboard is unaffected.'}
      </p>
    </div>
  )
}

const HEADLINE = { fontSize: 'clamp(2rem, 3.6vw, 3.25rem)', lineHeight: 1.08 }

/** One dashboard: the pitch on the left, the sign-in on the right — above the fold. */
function OneDashboard({ props }: { props: LoginProps }) {
  return (
    <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
      {/* Flex gaps, not space-y: .hero-headline and .hero-body set margin: 0. */}
      <div className="flex flex-col items-center gap-6 text-center lg:items-start lg:text-left">
        <span className="hero-eyebrow">Sign in</span>
        <h1 className="hero-headline max-w-xl" style={HEADLINE}>
          Welcome back to your{' '}
          <span className="text-white/50">music distribution dashboard.</span>
        </h1>
        <p className="hero-body max-w-xl">
          Releases, royalties, payouts, promo and support — all in one place.
        </p>
        <div className="mt-4 hidden border-t border-white/10 pt-8 lg:block">
          <FeatureList items={ONE_DASHBOARD_FEATURES} tone="accent" large />
        </div>
      </div>

      <div className="mx-auto w-full max-w-md space-y-5 lg:mx-0 lg:justify-self-end">
        <FlashMessages {...props.flash} />
        <section aria-labelledby="account-hub-title" className="marketing-surface-card" style={STILL_CARD}>
          <h2 id="account-hub-title" className="text-2xl font-bold tracking-tight text-white">Sign in</h2>
          <p className="mt-3 text-sm leading-6 text-white/70">
            Enter the email you applied with and we’ll send you a 6-digit code. No password needed.
          </p>
          <div className="mt-8">
            {props.maintenance ? <Maintenance twoDashboards={false} /> : <EmailForm props={props} />}
          </div>
          <div className="mt-8 border-t border-white/10 pt-6">
            <HelpLine props={props} stacked />
          </div>
        </section>
      </div>
    </div>
  )
}

/** Two dashboards: a short heading, then both doors side by side. */
function TwoDashboards({ props, url }: { props: LoginProps; url: string }) {
  return (
    <div className="mx-auto max-w-5xl space-y-10">
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center">
        <span className="hero-eyebrow">Sign in</span>
        <h1 className="hero-headline" style={HEADLINE}>
          Welcome back to your{' '}
          <span className="text-white/50">music distribution dashboard.</span>
        </h1>
        <p className="hero-body">wavbee runs across two dashboards. Pick the one you’re headed to.</p>
      </div>
      <div className="space-y-6">
        <FlashMessages {...props.flash} />
        <div className="grid gap-5 md:grid-cols-2">
          <DistributionDashboardCard url={url} />
          <AccountHubCard props={props} />
        </div>
        <HelpLine props={props} />
      </div>
    </div>
  )
}

export default function Login(props: LoginProps) {
  const dashboard = props.distribution_dashboard
  return (
    <>
      <AtmosphericHero minHeight="auto" containerClassName={`pt-8! lg:pt-10! ${dashboard ? '' : 'max-w-6xl'}`}>
        {dashboard ? <TwoDashboards props={props} url={dashboard.url} /> : <OneDashboard props={props} />}
      </AtmosphericHero>

      <section id="faq" className="marketing-section marketing-section-warm">
        <div className="container mx-auto max-w-3xl px-6">
          <div className="marketing-section-header">
            <div className="marketing-eyebrow-mono">FAQ</div>
            <h2 className="marketing-section-title marketing-section-title-dark">Signing in</h2>
          </div>
          <FaqAccordion items={faqItems(dashboard !== null)} />
        </div>
      </section>
    </>
  )
}
